import { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScreenerService } from "../../services/screener-service";
import type { FinancialStatement } from "@/schemas";

interface StatementsTabProps {
  assetId: string;
}

// User-friendly labels for raw yfinance keys
const LABEL_MAP: Record<string, string> = {
  // Income Statement
  "Total Revenue": "Revenue",
  "Operating Revenue": "Operating Revenue",
  "Cost Of Revenue": "Cost of Revenue",
  "Gross Profit": "Gross Profit",
  "Operating Expense": "Operating Expenses",
  "Selling General And Administration": "SG&A",
  "Research And Development": "R&D",
  "Operating Income": "Operating Income",
  "Net Non Operating Interest Income Expense": "Net Interest Income",
  "Other Income Expense": "Other Income/Expense",
  "Pretax Income": "Pre-tax Income",
  "Tax Provision": "Income Tax",
  "Net Income": "Net Income",
  "Net Income Common Stockholders": "Net Income (Common)",
  "Diluted EPS": "Diluted EPS",
  "Basic EPS": "Basic EPS",
  "EBITDA": "EBITDA",
  "EBIT": "EBIT",
  // Balance Sheet
  "Total Assets": "Total Assets",
  "Current Assets": "Current Assets",
  "Cash And Cash Equivalents": "Cash & Equivalents",
  "Cash Cash Equivalents And Short Term Investments": "Cash & Short-term Investments",
  "Accounts Receivable": "Accounts Receivable",
  "Inventory": "Inventory",
  "Total Non Current Assets": "Non-Current Assets",
  "Net PPE": "Property, Plant & Equipment",
  "Total Liabilities Net Minority Interest": "Total Liabilities",
  "Current Liabilities": "Current Liabilities",
  "Accounts Payable": "Accounts Payable",
  "Current Debt": "Short-term Debt",
  "Long Term Debt": "Long-term Debt",
  "Total Debt": "Total Debt",
  "Stockholders Equity": "Stockholders' Equity",
  "Retained Earnings": "Retained Earnings",
  "Common Stock": "Common Stock",
  "Share Issued": "Shares Outstanding",
  "Net Debt": "Net Debt",
  // Cash Flow
  "Operating Cash Flow": "Operating Cash Flow",
  "Cash Flow From Continuing Operating Activities": "Cash from Operations",
  "Net Income From Continuing Operations": "Net Income",
  "Depreciation And Amortization": "Depreciation & Amortization",
  "Stock Based Compensation": "Stock-Based Compensation",
  "Change In Working Capital": "Change in Working Capital",
  "Investing Cash Flow": "Investing Cash Flow",
  "Cash Flow From Continuing Investing Activities": "Cash from Investing",
  "Capital Expenditure": "Capital Expenditures",
  "Purchase Of Investment": "Investments Purchased",
  "Sale Of Investment": "Investments Sold",
  "Financing Cash Flow": "Financing Cash Flow",
  "Cash Flow From Continuing Financing Activities": "Cash from Financing",
  "Repurchase Of Capital Stock": "Stock Repurchases",
  "Cash Dividends Paid": "Dividends Paid",
  "Free Cash Flow": "Free Cash Flow",
};

// Get friendly label or format raw key
const getLabel = (rawKey: string): string => {
  if (LABEL_MAP[rawKey]) return LABEL_MAP[rawKey];
  // Convert camelCase or space-separated to Title Case
  return rawKey.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
};

// Format value for display
const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value !== "number") return String(value);
  
  const absValue = Math.abs(value);
  if (absValue >= 1e12) return `${(value / 1e12).toFixed(2)}T`;
  if (absValue >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (absValue >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (absValue >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
  return value.toFixed(2);
};

// Priority order for metrics (most important first)
const INCOME_PRIORITY = [
  "Total Revenue", "Cost Of Revenue", "Gross Profit", "Operating Expense",
  "Selling General And Administration", "Research And Development", "Operating Income",
  "EBITDA", "EBIT", "Pretax Income", "Tax Provision", "Net Income", "Diluted EPS", "Basic EPS"
];

const BALANCE_PRIORITY = [
  "Total Assets", "Current Assets", "Cash And Cash Equivalents", "Accounts Receivable",
  "Inventory", "Total Non Current Assets", "Net PPE", "Total Liabilities Net Minority Interest",
  "Current Liabilities", "Accounts Payable", "Total Debt", "Long Term Debt", "Current Debt",
  "Stockholders Equity", "Retained Earnings", "Share Issued", "Net Debt"
];

const CASHFLOW_PRIORITY = [
  "Operating Cash Flow", "Net Income From Continuing Operations", "Depreciation And Amortization",
  "Stock Based Compensation", "Change In Working Capital", "Investing Cash Flow",
  "Capital Expenditure", "Purchase Of Investment", "Sale Of Investment", "Financing Cash Flow",
  "Repurchase Of Capital Stock", "Cash Dividends Paid", "Free Cash Flow"
];

// Sort metrics by priority
const sortMetrics = (keys: string[], priority: string[]): string[] => {
  const priorityMap = new Map(priority.map((k, i) => [k, i]));
  return [...keys].sort((a, b) => {
    const aIdx = priorityMap.get(a) ?? 999;
    const bIdx = priorityMap.get(b) ?? 999;
    return aIdx - bIdx;
  });
};

// Statement table component
function StatementTable({
  statements,
  priority,
  periodType,
}: {
  statements: FinancialStatement[];
  priority: string[];
  periodType: "annual" | "quarterly";
}) {
  // Ensure statements is always an array
  const safeStatements = Array.isArray(statements) ? statements : [];

  // Get all unique metrics across statements
  const allMetrics = useMemo(() => {
    const metrics = new Set<string>();
    safeStatements.forEach((s) => {
      Object.keys(s.metrics || {}).forEach((k) => metrics.add(k));
    });
    return sortMetrics([...metrics], priority);
  }, [safeStatements, priority]);

  // Sort statements by date (most recent first)
  const sortedStatements = useMemo(() => {
    return [...safeStatements].sort(
      (a, b) => new Date(b.period_end_date).getTime() - new Date(a.period_end_date).getTime()
    );
  }, [safeStatements]);

  const getPeriodLabel = (statement: FinancialStatement): string => {
    const year = new Date(statement.period_end_date).getFullYear();
    if (periodType === "quarterly" && statement.fiscal_quarter) {
      return `${statement.fiscal_quarter} ${year}`;
    }
    return `FY ${year}`;
  };

  if (safeStatements.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground">
        No data available
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2 px-3 font-medium sticky left-0 bg-background">Metric</th>
            {sortedStatements.map((s) => (
              <th key={s.period_end_date} className="text-right py-2 px-3 font-medium min-w-[100px]">
                {getPeriodLabel(s)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {allMetrics.map((metric) => (
            <tr key={metric} className="border-b border-border/50 hover:bg-muted/30">
              <td className="py-2 px-3 sticky left-0 bg-background text-muted-foreground">
                {getLabel(metric)}
              </td>
              {sortedStatements.map((s) => {
                const value = s.metrics?.[metric];
                const isNegative = typeof value === "number" && value < 0;
                return (
                  <td
                    key={s.period_end_date}
                    className={`text-right py-2 px-3 tabular-nums ${isNegative ? "text-red-500" : ""}`}
                  >
                    {formatValue(value)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function StatementsTab({ assetId }: StatementsTabProps) {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<"annual" | "quarterly">("annual");
  const [incomeStatements, setIncomeStatements] = useState<FinancialStatement[]>([]);
  const [balanceSheets, setBalanceSheets] = useState<FinancialStatement[]>([]);
  const [cashFlows, setCashFlows] = useState<FinancialStatement[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStatements = async () => {
      setLoading(true);
      setError(null);
      try {
        const [income, balance, cashflow] = await Promise.all([
          ScreenerService.getFinancialStatements(assetId, "income_statement", period),
          ScreenerService.getFinancialStatements(assetId, "balance_sheet", period),
          ScreenerService.getFinancialStatements(assetId, "cashflow", period),
        ]);
        setIncomeStatements(income.statements || income || []);
        setBalanceSheets(balance.statements || balance || []);
        setCashFlows(cashflow.statements || cashflow || []);
      } catch (err) {
        console.error("Failed to fetch statements:", err);
        setError("Failed to load financial statements");
      } finally {
        setLoading(false);
      }
    };

    fetchStatements();
  }, [assetId, period]);

  // CSV export handler
  const handleExport = (statements: FinancialStatement[], filename: string) => {
    if (statements.length === 0) return;

    const allMetrics = new Set<string>();
    statements.forEach((s) => Object.keys(s.metrics || {}).forEach((k) => allMetrics.add(k)));

    const sortedStatements = [...statements].sort(
      (a, b) => new Date(b.period_end_date).getTime() - new Date(a.period_end_date).getTime()
    );

    const getPeriodLabel = (statement: FinancialStatement): string => {
      const year = new Date(statement.period_end_date).getFullYear();
      if (period === "quarterly" && statement.fiscal_quarter) {
        return `${statement.fiscal_quarter} ${year}`;
      }
      return `FY ${year}`;
    };

    const headers = ["Metric", ...sortedStatements.map((s) => getPeriodLabel(s))];
    const rows = [...allMetrics].map((metric) => [
      getLabel(metric),
      ...sortedStatements.map((s) => {
        const val = s.metrics?.[metric];
        return val !== undefined && val !== null ? String(val) : "";
      }),
    ]);

    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}_${period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Tabs defaultValue="income">
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <TabsList variant="line" className="w-auto max-w-full">
            <TabsTrigger value="income" className="text-xs px-3">Income Statement</TabsTrigger>
            <TabsTrigger value="balance" className="text-xs px-3">Balance Sheet</TabsTrigger>
            <TabsTrigger value="cashflow" className="text-xs px-3">Cash Flow</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            <Tabs value={period} onValueChange={(v) => setPeriod(v as "annual" | "quarterly")}>
              <TabsList variant="line">
                <TabsTrigger value="annual" className="min-h-8 text-xs px-2 py-1.5">Annual</TabsTrigger>
                <TabsTrigger value="quarterly" className="min-h-8 text-xs px-2 py-1.5">Quarterly</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        <TabsContent value="income">
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between p-3 border-b">
                <span className="text-sm font-medium">Income Statement</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleExport(incomeStatements, "income_statement")}
                  disabled={incomeStatements.length === 0}
                >
                  <Download className="h-3 w-3 mr-1" />
                  CSV
                </Button>
              </div>
              <StatementTable statements={incomeStatements} priority={INCOME_PRIORITY} periodType={period} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="balance">
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between p-3 border-b">
                <span className="text-sm font-medium">Balance Sheet</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleExport(balanceSheets, "balance_sheet")}
                  disabled={balanceSheets.length === 0}
                >
                  <Download className="h-3 w-3 mr-1" />
                  CSV
                </Button>
              </div>
              <StatementTable statements={balanceSheets} priority={BALANCE_PRIORITY} periodType={period} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cashflow">
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between p-3 border-b">
                <span className="text-sm font-medium">Cash Flow Statement</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleExport(cashFlows, "cashflow")}
                  disabled={cashFlows.length === 0}
                >
                  <Download className="h-3 w-3 mr-1" />
                  CSV
                </Button>
              </div>
              <StatementTable statements={cashFlows} priority={CASHFLOW_PRIORITY} periodType={period} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
